// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useMemo } from "react";
import { CalendarOff, CircleCheck, Database } from "lucide-react";
import { TYPE_CONFIG } from "./constants";

const STATUS_LABELS = {
  confirmed: "Bestätigt",
  pending: "Ausstehend",
  cancelled: "Storniert",
  canceled: "Storniert",
};

const statusLabel = (status) =>
  STATUS_LABELS[(status || "").toLowerCase()] || status || "Bestätigt";

const AvailabilityList = ({ filteredData, onRowClick }) => {
  const rows = useMemo(() => {
    const sorted = [...filteredData].sort((a, b) =>
      a.date.localeCompare(b.date),
    );
    const result = [];
    let lastMonth = "";

    sorted.forEach((item) => {
      const d = new Date(item.date);
      const monthLabel = d.toLocaleDateString("de-DE", {
        month: "long",
        year: "numeric",
      });
      if (monthLabel !== lastMonth) {
        result.push({ type: "header", key: monthLabel, label: monthLabel });
        lastMonth = monthLabel;
      }
      result.push({ type: "row", key: item.id, item });
    });

    return result;
  }, [filteredData]);

  if (!filteredData.length) {
    return (
      <div className="av-list-wrap">
        <div className="av-empty-state">
          <CalendarOff size={36} aria-hidden />
          <p>Keine belegten Tage für diese Filterauswahl.</p>
        </div>
      </div>
    );
  }

  return (
    <ul className="av-list-wrap" role="list" aria-label="Belegte Tage">
      {rows.map((row) => {
        if (row.type === "header") {
          return (
            <li
              key={row.key}
              role="listitem"
              className="av-list-month-header"
              aria-label={`Monat: ${row.label}`}
            >
              {row.label}
            </li>
          );
        }

        const item = row.item;
        const cfg = TYPE_CONFIG[item.type];
        const Icon = cfg.Icon;
        const d = new Date(item.date);
        const dayLabel = d.toLocaleDateString("de-DE", {
          weekday: "short",
          day: "numeric",
          month: "short",
        });

        return (
          <li key={row.key} role="listitem">
            <div
              className="av-list-row"
              role="button"
              tabIndex={0}
              aria-label={`${dayLabel}: ${item.title}, ${cfg.label}${item.source === "seas" ? ", SEAS" : ""}`}
              onClick={() => onRowClick(item.date)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onRowClick(item.date);
                }
              }}
            >
              <div className="av-list-col-date">
                <div className="av-list-date">{dayLabel}</div>
              </div>
              <div className="av-list-col-badge">
                <span
                  className="av-type-badge"
                  style={{
                    background: cfg.bg,
                    color: cfg.text,
                    borderColor: cfg.border,
                  }}
                >
                  <Icon size={12} aria-hidden />
                  {cfg.label}
                </span>
              </div>
              <div className="av-list-col-info">
                <div className="av-list-title">{item.title}</div>
                <div className="av-list-sub">
                  {item.org} · {item.time}
                </div>
                {item.source === "seas" && (
                  <div className="av-seas-tag-list">
                    <Database size={10} aria-hidden />
                    SEAS · {item.seasNote || ""}
                  </div>
                )}
              </div>
              <div className="av-list-col-meta av-list-meta">
                <div>{item.ref}</div>
                <div className="av-list-confirmed">
                  <CircleCheck size={12} aria-hidden />
                  {statusLabel(item.status)}
                </div>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default AvailabilityList;
